import { Injectable } from '@angular/core';
import { SymbolType } from '../../../../../../domain/symbol.domain';
import { TradingAccount } from '../../../../../../domain/account.domain';
import { OrderSecurity } from '../../../../../../domain/security.domain';
import { BackendMapper } from '../../../domain/services.domain';
import {
  MultiLegSecurityType,
  OrderItem,
  OrderRequest,
  PricePlan,
  PriceType,
  Strategy,
  TradingCredential
} from '../../../../domain';
import { CreateOptionOrderMapper } from './create-option-order-mapper.service';
import { CreateOrderMapper } from './create-order-mapper.service';
import { CreateStockOrderMapper } from './create-stock-order-mapper.service';
import type {
  EquityFormModel,
  MultilegFormModel,
  MultilegItemFormModel,
  OptionFormModel,
  StrategyFormModel
} from '../../../domain/order-entry.domain';

@Injectable()
export class CreateStrategyOrderMapper
  extends CreateOrderMapper<StrategyFormModel | MultilegFormModel>
  implements BackendMapper<StrategyFormModel> {

  constructor(private createStockMapper: CreateStockOrderMapper,
              private createOptionMapper: CreateOptionOrderMapper) {
    super();
  }

  getMapperType(): OrderSecurity {
    return 'strategy';
  }

  createCaptureRequest(form: StrategyFormModel | MultilegFormModel,
                       selectedAccount: TradingAccount,
                       tradingCredential: TradingCredential,
                       strategy: Strategy = Strategy.SPL): OrderRequest {

    let items: Array<OrderItem>;
    switch (strategy) {
      case Strategy.OCO:
        items = this.getOcoItems(form);
        break;
      case Strategy.OTA:
        items = this.getOtaItems(form);
        break;
      case Strategy.FTO:
        items = this.getFtoItems(form);
        break;
      case Strategy.MLO:
        items = this.getMloItems(form as MultilegFormModel);
        break;
    }

    const request = strategy === Strategy.MLO ?
      this.createCaptureRequestMultileg(form as MultilegFormModel, items, selectedAccount) :
      this.createCaptureRequestStrategy(items, selectedAccount, strategy);

    if (tradingCredential !== undefined) {
      request.tradingCredential = tradingCredential;
    }

    return request;
  }

  // unused

  getItems(): Array<OrderItem> {
    return [];
  }

  private createCaptureRequestStrategy(items: Array<OrderItem>,
                                       selectedAccount: TradingAccount,
                                       strategy: Strategy): OrderRequest {
    return {
      orderTerms: {
        accountInfo: { accountId: selectedAccount.id },
        afterMarket: false,
        order: items,
        pricePlan: PricePlan.MARKET,
        strategy
      },
      conditionFilter: 'STANDARD'
    } as OrderRequest;
  }

  private createCaptureRequestMultileg(
    form: MultilegFormModel,
    items: Array<OrderItem>,
    selectedAccount: TradingAccount): OrderRequest {

    return {
      orderTerms: {
        strategy: Strategy.MLO,
        order: items,
        accountInfo: {
          accountId: selectedAccount.id
        },
        pricePlan: form.pricePlan,
        afterMarket: false,
        multilegEntry: {
          extendedTerms: {
            goodTill: form.goodTill,
            goodTillDate: form.goodTillDate,
            limitPrice: form.limitPrice
          }
        }
      },
      conditionFilter: 'STANDARD'
    };
  }

  private getOcoItems(formModel: StrategyFormModel): Array<OrderItem> {
    const firstItem = this.createStockMapper.getItems(formModel.items[0] as EquityFormModel)[0];
    const secondItem = this.createStockMapper.getItems(formModel.items[1] as EquityFormModel)[0];
    return [firstItem, secondItem];
  }

  private getOtaItems(formModel: StrategyFormModel): Array<OrderItem> {
    const firstItem = ('optionQuoteParams' in formModel.items[0]) ?
      this.createOptionMapper.getItems(formModel.items[0] as OptionFormModel)[0] :
      this.createStockMapper.getItems(formModel.items[0] as EquityFormModel)[0];
    const secondItem = ('optionQuoteParams' in formModel.items[1]) ?
      this.createOptionMapper.getItems(formModel.items[1] as OptionFormModel)[0] :
      this.createStockMapper.getItems(formModel.items[1] as EquityFormModel)[0];
    return [firstItem, secondItem];
  }

  private getFtoItems(formModel: StrategyFormModel): Array<OrderItem> {
    const firstItem = this.createStockMapper.getItems(formModel.items[0] as EquityFormModel)[0];
    const secondItem = this.createStockMapper.getItems(formModel.items[1] as EquityFormModel)[0];
    const thirdItem = this.createStockMapper.getItems(formModel.items[2] as EquityFormModel)[0];
    return [firstItem, secondItem, thirdItem];
  }

  private getMloItems(formModel: MultilegFormModel): Array<OrderItem> {
    const firstLeg = this.getLeg(formModel, formModel.firstLeg, formModel.selectedType);
    const secondLeg = this.getLeg(formModel, formModel.secondLeg, SymbolType.OPTION);
    return [firstLeg, secondLeg];
  }

  private getLeg(formModel: MultilegFormModel,
                 leg: MultilegItemFormModel,
                 selectedType: MultiLegSecurityType): OrderItem {
    return {
      id: leg.id,
      quantity: leg.orderQuantity,
      extendedTerms: {
        orderType: PriceType.LIMIT,
        orderAction: leg.orderAction,
        allOrNone: false
      },
      clientInfo: {
        insiderInfo: null,
        solicited: false
      },
      security: {
        type: selectedType,
        market: formModel.selectedSymbol.market,
        symbol: formModel.selectedSymbol.symbol,
        displayTicker: selectedType === SymbolType.OPTION ? leg.optionQuoteParams.displayTicker : undefined
      }
    };
  }
}

